import { parseLaunchEnvironment, parseDeviceStateKey } from "./device-operations";
import type { Limrun } from "@limrun/api";
import { IOS_MODEL_LABEL, parseIOSModel, type IOSModel } from "./ios-model";

// Mirrors the iOS version/model pairs accepted by director's pool selection.
export const IOS_VERSIONS = {
  'iOS 26': { osVersion: '26', models: ['iphone', 'ipad', 'watch'] },
  'iOS 27': { osVersion: '27', models: ['iphone', 'ipad', 'iphone-duo'] },
} as const satisfies Record<string, { osVersion: string; models: readonly IOSModel[] }>;

export type PlaygroundConfiguration = {
  assets: string[];
  labels: Record<string, string>;
  tunnel?: string;
  env?: Record<string, string>;
  openUrl?: string;
  inactivityTimeout?: string;
  deviceState?: string;
} & (
  | { platform: "ios"; osVersion: string; model: IOSModel }
  | { platform: "android"; osVersion: string; model: "phone" | "tablet" }
);

// Console launches install apps after the device is ready. CLI copies retain --install-asset.
export function playgroundRequest(configuration: PlaygroundConfiguration):
  | { platform: "ios"; params: Limrun.IosInstanceCreateParams }
  | { platform: "android"; params: Limrun.AndroidInstanceCreateParams } {
  const spec = {
    clues: [{ kind: "OSVersion" as const, osVersion: configuration.osVersion }],
    ...(configuration.inactivityTimeout && { inactivityTimeout: configuration.inactivityTimeout }),
    ...(configuration.tunnel && { tunnel: configuration.tunnel }),
  };
  if (configuration.platform === "ios") {
    return {
      platform: "ios",
      params: {
        wait: true,
        metadata: { labels: { ...configuration.labels, [IOS_MODEL_LABEL]: configuration.model } },
        spec: { ...spec, model: configuration.model },
      },
    };
  }
  return { platform: "android", params: {
    wait: true,
    ...(Object.keys(configuration.labels).length > 0 && { metadata: { labels: configuration.labels } }),
    spec: { ...spec, model: configuration.model },
  } };
}

export function parseInstanceLabels(entries: string[]): Record<string, string> {
  return Object.fromEntries(entries.map(entry => {
    const separator = entry.indexOf("=");
    if (separator < 1) throw new Error("Labels must use key=value, one per line.");
    const key = entry.slice(0, separator).trim();
    if (!key) throw new Error("Label keys cannot be empty.");
    if (key === IOS_MODEL_LABEL) throw new Error("The console's device model label is set automatically.");
    return [key, entry.slice(separator + 1)];
  }));
}

function shellQuote(value: string): string {
  return /^[a-zA-Z0-9_./:=@+-]+$/.test(value) ? value : `'${value.replace(/'/g, "'\"'\"'")}'`;
}

export function playgroundCommand(configuration: PlaygroundConfiguration): string {
  if (Object.keys(configuration.env ?? {}).length || configuration.openUrl || configuration.deviceState) throw new Error("Use Copy as URL to include launch environment variables, the URL to open, and saved device state.");
  const args = ["lim", configuration.platform, "create", "--os-version", configuration.osVersion, "--model", configuration.model];
  if (configuration.inactivityTimeout) args.push("--inactivity-timeout", configuration.inactivityTimeout);
  if (configuration.platform === "ios") args.push("--label", `${IOS_MODEL_LABEL}=${configuration.model}`);
  const command = args.map(shellQuote);
  for (const [key, value] of Object.entries(configuration.labels)) command.push(`--label=${shellQuote(`${key}=${value}`)}`);
  for (const asset of configuration.assets) command.push(`--install-asset=${shellQuote(asset)}`);
  if (configuration.tunnel) command.push(`--tunnel=${shellQuote(configuration.tunnel)}`);
  return command.join(" ");
}

export function playgroundPreviewUrl(configuration: PlaygroundConfiguration, organizationId: string | undefined, origin: string): string {
  const url = new URL("/stream", origin);
  url.search = new URLSearchParams({
    platform: configuration.platform, osVersion: configuration.osVersion,
    model: configuration.model, inactivityTimeout: configuration.inactivityTimeout ?? "",
  }).toString();
  if (organizationId) url.searchParams.set("organization", organizationId);
  for (const asset of configuration.assets) url.searchParams.append("asset", asset);
  for (const [key, value] of Object.entries(configuration.labels)) url.searchParams.append("label", `${key}=${value}`);
  if (configuration.tunnel) url.searchParams.set("tunnel", configuration.tunnel);
  for (const [key, value] of Object.entries(configuration.env ?? {})) url.searchParams.append("env", `${key}=${value}`);
  if (configuration.deviceState) url.searchParams.set("snapshot-key", configuration.deviceState);
  if (configuration.openUrl) url.searchParams.set("openUrl", configuration.openUrl);
  return url.toString();
}

export function parsePlaygroundPreview(params: URLSearchParams): { configuration: PlaygroundConfiguration; organizationId?: string } {
  const organizationId = params.get("organization") || undefined;
  return { configuration: parsePlaygroundConfiguration(params), organizationId };
}

export function parsePlaygroundConfiguration(params: URLSearchParams): PlaygroundConfiguration {
  // Older copied links use mode to preserve the organization's default timeout.
  const legacyPlaygroundLink = params.get("mode") === "playground";
  const assets = params.getAll("asset");
  const suppliedModel = params.get("model")?.toLowerCase();
  const platform = params.get("platform") ?? params.get("os")?.toLowerCase() ?? (suppliedModel && !parseIOSModel(suppliedModel) ? "android" : "ios");
  const model = suppliedModel || (platform === "ios" ? "iphone" : "phone");
  // Use the first supported iOS version so model-only links match the console selection.
  const osVersion = params.get("osVersion") || (platform === "ios"
    ? Object.values(IOS_VERSIONS).find(version => (version.models as readonly string[]).includes(model))?.osVersion ?? ""
    : "15");
  const normalized = new URLSearchParams(params);
  normalized.set("platform", platform);
  const labels = parseInstanceLabels(params.getAll("label"));
  const tunnel = params.get("tunnel") || undefined;
  const env = parseLaunchEnvironment(normalized);
  const openUrl = params.get("openUrl") || undefined;
  const inactivityTimeout = params.has("inactivityTimeout") ? params.get("inactivityTimeout") || undefined
    : !legacyPlaygroundLink && (assets.length || parseDeviceStateKey(normalized)) ? "5m" : undefined;
  const deviceState = parseDeviceStateKey(normalized);
  const launch = { ...(deviceState && { deviceState }), ...(Object.keys(env).length > 0 && { env }), ...(openUrl && { openUrl }), ...(inactivityTimeout && { inactivityTimeout }) };
  if (assets.some(asset => !asset.trim())) throw new Error("The preview link contains an empty app name.");
  if (tunnel && !/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/.test(tunnel)) {
    throw new Error("The preview link contains an invalid tunnel name.");
  }
  if (platform === "ios" && Object.values(IOS_VERSIONS).some(version =>
    version.osVersion === osVersion && (version.models as readonly string[]).includes(model))) {
    return { platform, osVersion, model: model as IOSModel, assets, labels, tunnel, ...launch };
  }
  if (platform === "android" && ["14", "15"].includes(osVersion) &&
      (model === "phone" || (model === "tablet" && osVersion !== "14"))) {
    return { platform, osVersion, model, assets, labels, tunnel, ...launch };
  }
  throw new Error("The preview link contains an unsupported device configuration.");
}

export function hasPlaygroundConfiguration(params: URLSearchParams): boolean {
  return ["platform", "os", "osVersion", "model", "asset", "label", "tunnel", "env", "openUrl", "inactivityTimeout", "organization", "mode", "deviceState", "snapshot-key", "simulatorSnapshot"].some(key => params.has(key));
}
